/**
 * Auto-Fill Utilities
 * Centralized auto-fill logic for form data population
 */

// Common field mappings for auto-fill relationships
export const FIELD_MAPPINGS = {
    // Contact Information
    contact: {
        primary_contact_name: ['president_name', 'contact_name', 'name'],
        primary_contact_email: ['president_email', 'contact_email', 'email'],
        phone_number: ['phone', 'contact_phone', 'telephone'],
        website: ['web_site', 'url', 'homepage']
    },

    // Organization Information
    organization: {
        organization_name: ['company_name', 'business_name', 'org_name'],
        address: ['street_address', 'physical_address', 'location'],
        city: ['town', 'municipality'],
        state: ['province', 'region', 'territory'],
        postal_code: ['zip_code', 'postcode', 'zip'],
        country: ['nation', 'territory']
    },

    // Financial Information
    financial: {
        amount: ['price', 'cost', 'fee', 'value'],
        invoice_type: ['type', 'category', 'classification'],
        description: ['notes', 'comments', 'details', 'remarks']
    },

    // Member Information
    member: {
        member_id: ['id', 'reference', 'code'],
        membership_status: ['status', 'state', 'condition'],
        screening_format: ['format', 'type', 'media_type'],
        publication_preferences: ['preferences', 'subscription', 'mailing']
    }
};

// Auto-fill patterns for different data types
export const AUTO_FILL_PATTERNS = {
    // Email patterns
    email: {
        from: ['president_email', 'contact_email', 'email'],
        to: ['primary_contact_email', 'email', 'contact_email']
    },

    // Name patterns
    name: {
        from: ['president_name', 'contact_name', 'name', 'organization_name'],
        to: ['primary_contact_name', 'name', 'contact_name', 'president_name']
    },

    // Address patterns
    address: {
        from: ['address', 'street_address', 'location'],
        to: ['address', 'street_address', 'physical_address']
    },

    // Phone patterns
    phone: {
        from: ['phone_number', 'phone', 'contact_phone'],
        to: ['phone_number', 'phone', 'telephone']
    }
};

/**
 * Find related field values based on input field and existing data
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing records
 * @param {Object} currentFormData - Current form data
 * @returns {Object} Auto-fill suggestions
 */
export const findAutoFillSuggestions = (fieldName, fieldValue, existingData = [], currentFormData = {}) => {
    if (!fieldValue || fieldValue.trim() === '' || existingData.length === 0) {
        return {};
    }

    const suggestions = {};
    const searchValue = fieldValue.toLowerCase().trim();

    // Limit search to prevent performance issues
    const maxRecords = Math.min(existingData.length, 100);
    const dataToSearch = existingData.slice(0, maxRecords);

    // Find matching records with early exit for performance
    const matchingRecords = [];
    for (let i = 0; i < dataToSearch.length && matchingRecords.length < 10; i++) {
        const record = dataToSearch[i];
        const hasMatch = Object.values(record).some(value =>
            value && value.toString().toLowerCase().includes(searchValue)
        );
        if (hasMatch) {
            matchingRecords.push(record);
        }
    }

    if (matchingRecords.length === 0) {
        return suggestions;
    }

    // Get the most recent or most complete record
    const bestMatch = matchingRecords.reduce((best, current) => {
        const currentScore = calculateMatchScore(current, searchValue);
        const bestScore = calculateMatchScore(best, searchValue);
        return currentScore > bestScore ? current : best;
    });

    // Generate suggestions based on field mappings - only for the current field
    Object.entries(FIELD_MAPPINGS).forEach(([category, mappings]) => {
        Object.entries(mappings).forEach(([targetField, sourceFields]) => {
            if (sourceFields.includes(fieldName)) {
                // Find the best matching source field in the record
                for (const sourceField of sourceFields) {
                    if (bestMatch[sourceField] && bestMatch[sourceField] !== fieldValue) {
                        suggestions[targetField] = bestMatch[sourceField];
                        break;
                    }
                }
            }
        });
    });

    // Add direct field suggestions (exact matches for the current field)
    if (bestMatch[fieldName] && bestMatch[fieldName] !== fieldValue) {
        suggestions[fieldName] = bestMatch[fieldName];
    }

    // Special handling for specific field types - only add if not already present
    if (fieldName.includes('email') || fieldName.includes('Email')) {
        if (!suggestions.primary_contact_email) {
            suggestions.primary_contact_email = bestMatch.primary_contact_email || bestMatch.president_email || bestMatch.email;
        }
    }

    if (fieldName.includes('name') || fieldName.includes('Name')) {
        if (!suggestions.primary_contact_name) {
            suggestions.primary_contact_name = bestMatch.primary_contact_name || bestMatch.president_name || bestMatch.name;
        }
    }

    if (fieldName.includes('organization') || fieldName.includes('Organization')) {
        if (!suggestions.organization_name) {
            suggestions.organization_name = bestMatch.organization_name || bestMatch.company_name || bestMatch.business_name;
        }
    }

    if (fieldName.includes('phone') || fieldName.includes('Phone')) {
        if (!suggestions.phone_number) {
            suggestions.phone_number = bestMatch.phone_number || bestMatch.phone || bestMatch.contact_phone;
        }
    }

    if (fieldName.includes('address') || fieldName.includes('Address')) {
        if (!suggestions.address) {
            suggestions.address = bestMatch.address || bestMatch.street_address || bestMatch.location;
        }
        if (!suggestions.city) {
            suggestions.city = bestMatch.city || bestMatch.town || bestMatch.municipality;
        }
        if (!suggestions.state) {
            suggestions.state = bestMatch.state || bestMatch.province || bestMatch.region;
        }
        if (!suggestions.postal_code) {
            suggestions.postal_code = bestMatch.postal_code || bestMatch.zip_code || bestMatch.postcode;
        }
    }

    // Filter out suggestions that are already filled or empty
    const filteredSuggestions = {};
    Object.entries(suggestions).forEach(([key, value]) => {
        if (value && value !== currentFormData[key] && value.trim() !== '') {
            filteredSuggestions[key] = value;
        }
    });

    return filteredSuggestions;
};


/**
 * Smart auto-fill for organization data
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing records
 * @returns {Object} Organization-related suggestions
 */
export const getOrganizationAutoFill = (fieldName, fieldValue, existingData = []) => {
    const suggestions = findAutoFillSuggestions(fieldName, fieldValue, existingData);

    // Additional organization-specific logic
    if (fieldName.includes('organization') || fieldName.includes('company')) {
        const matchingOrgs = existingData.filter(record =>
            record.organization_name &&
            record.organization_name.toLowerCase().includes(fieldValue.toLowerCase())
        );

        if (matchingOrgs.length > 0) {
            const org = matchingOrgs[0];
            return {
                organization_name: org.organization_name,
                address: org.address,
                city: org.city,
                state: org.state,
                postal_code: org.postal_code,
                country: org.country || 'Australia',
                website: org.website,
                primary_contact_name: org.primary_contact_name || org.president_name,
                primary_contact_email: org.primary_contact_email || org.president_email,
                phone_number: org.phone_number || org.phone
            };
        }
    }

    return suggestions;
};

/**
 * Smart auto-fill for contact information
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing records
 * @returns {Object} Contact-related suggestions
 */
export const getContactAutoFill = (fieldName, fieldValue, existingData = []) => {
    const suggestions = findAutoFillSuggestions(fieldName, fieldValue, existingData);

    // Additional contact-specific logic
    if (fieldName.includes('email') || fieldName.includes('Email')) {
        const matchingContacts = existingData.filter(record =>
            (record.primary_contact_email || record.president_email || record.email) &&
            (record.primary_contact_email || record.president_email || record.email)
                .toLowerCase().includes(fieldValue.toLowerCase())
        );

        if (matchingContacts.length > 0) {
            const contact = matchingContacts[0];
            return {
                primary_contact_name: contact.primary_contact_name || contact.president_name,
                primary_contact_email: contact.primary_contact_email || contact.president_email,
                phone_number: contact.phone_number || contact.phone,
                organization_name: contact.organization_name
            };
        }
    }

    return suggestions;
};

/**
 * Smart auto-fill for financial data
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing records
 * @returns {Object} Financial-related suggestions
 */
export const getFinancialAutoFill = (fieldName, fieldValue, existingData = []) => {
    const suggestions = findAutoFillSuggestions(fieldName, fieldValue, existingData);

    // Additional financial-specific logic
    if (fieldName.includes('member') || fieldName.includes('Member')) {
        const matchingMembers = existingData.filter(record =>
            record.member_id &&
            record.member_id.toLowerCase().includes(fieldValue.toLowerCase())
        );

        if (matchingMembers.length > 0) {
            const member = matchingMembers[0];
            return {
                member_id: member.member_id,
                organization_name: member.organization_name,
                primary_contact_name: member.primary_contact_name || member.president_name,
                primary_contact_email: member.primary_contact_email || member.president_email,
                address: member.address,
                city: member.city,
                state: member.state
            };
        }
    }

    return suggestions;
};

/**
 * Validate auto-fill suggestions
 * @param {Object} suggestions - Auto-fill suggestions
 * @param {Object} validationRules - Validation rules for the form
 * @returns {Object} Validated suggestions
 */
export const validateAutoFillSuggestions = (suggestions, validationRules = {}) => {
    const validatedSuggestions = {};

    Object.entries(suggestions).forEach(([field, value]) => {
        const rules = validationRules[field];
        if (!rules) {
            validatedSuggestions[field] = value;
            return;
        }

        // Basic validation
        if (rules.required && (!value || value.trim() === '')) {
            return; // Skip invalid suggestions
        }

        if (rules.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return; // Skip invalid email
        }

        if (rules.minLength && value && value.length < rules.minLength) {
            return; // Skip too short values
        }

        if (rules.maxLength && value && value.length > rules.maxLength) {
            validatedSuggestions[field] = value.substring(0, rules.maxLength);
            return;
        }

        validatedSuggestions[field] = value;
    });

    return validatedSuggestions;
};

/**
 * Get distributor-specific auto-fill suggestions
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing distributors
 * @returns {Object} Auto-fill suggestions
 */
const getDistributorAutoFill = (fieldName, fieldValue, existingData) => {
    const suggestions = {};
    const fieldMappings = {
        name: ['name', 'company_name', 'organization_name'],
        contact_person: ['contact_person', 'contact_name', 'representative'],
        email: ['email', 'contact_email', 'business_email'],
        phone: ['phone', 'contact_phone', 'business_phone'],
        address: ['address', 'business_address', 'location'],
        website: ['website', 'url', 'web_address']
    };

    // Find matching distributors with scoring
    const matchingDistributors = existingData
        .map(distributor => {
            const searchFields = fieldMappings[fieldName] || [fieldName];
            let bestScore = 0;
            let bestValue = null;

            searchFields.forEach(field => {
                if (distributor[field]) {
                    const score = calculateMatchScore(fieldValue, distributor[field], fieldName);
                    if (score > bestScore) {
                        bestScore = score;
                        bestValue = distributor[field];
                    }
                }
            });

            return { distributor, score: bestScore, value: bestValue };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score); // Sort by score descending

    if (matchingDistributors.length > 0) {
        // Get the best match (highest score)
        const bestMatch = matchingDistributors[0];

        // Only return suggestion for the current field being typed
        if (fieldName === 'name' && bestMatch.distributor.name) {
            suggestions.name = bestMatch.distributor.name;
        } else if (fieldName === 'contact_person' && bestMatch.distributor.contact_person) {
            suggestions.contact_person = bestMatch.distributor.contact_person;
        } else if (fieldName === 'email' && bestMatch.distributor.email) {
            suggestions.email = bestMatch.distributor.email;
        } else if (fieldName === 'phone' && bestMatch.distributor.phone) {
            suggestions.phone = bestMatch.distributor.phone;
        } else if (fieldName === 'address' && bestMatch.distributor.address) {
            suggestions.address = bestMatch.distributor.address;
        } else if (fieldName === 'website' && bestMatch.distributor.website) {
            suggestions.website = bestMatch.distributor.website;
        }
    }

    return suggestions;
};

/**
 * Get application-specific auto-fill suggestions
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing applications
 * @returns {Object} Auto-fill suggestions
 */
const getApplicationAutoFill = (fieldName, fieldValue, existingData) => {
    const suggestions = {};
    const fieldMappings = {
        organization_name: ['organization_name', 'company_name', 'name'],
        current_members_count: ['current_members_count', 'members_count', 'member_count'],
        president_name: ['president_name', 'contact_person', 'representative'],
        president_email: ['president_email', 'contact_email', 'email'],
        president_phone: ['president_phone', 'contact_phone', 'phone'],
        secretary_name: ['secretary_name', 'contact_person', 'representative'],
        secretary_email: ['secretary_email', 'contact_email', 'email'],
        secretary_phone: ['secretary_phone', 'contact_phone', 'phone'],
        treasurer_name: ['treasurer_name', 'contact_person', 'representative'],
        treasurer_email: ['treasurer_email', 'contact_email', 'email'],
        treasurer_phone: ['treasurer_phone', 'contact_phone', 'phone'],
        street_address_1: ['street_address_1', 'address', 'business_address'],
        street_address_2: ['street_address_2', 'address_2'],
        city: ['city', 'town', 'municipality'],
        state: ['state', 'province', 'region'],
        postcode: ['postcode', 'postal_code', 'zip_code'],
        screening_venue: ['screening_venue', 'venue', 'location']
    };

    // Find matching applications with scoring
    const matchingApplications = existingData
        .map(application => {
            const searchFields = fieldMappings[fieldName] || [fieldName];
            let bestScore = 0;
            let bestValue = null;

            searchFields.forEach(field => {
                if (application[field]) {
                    const score = calculateMatchScore(fieldValue, application[field].toString(), fieldName);
                    if (score > bestScore) {
                        bestScore = score;
                        bestValue = application[field];
                    }
                }
            });

            return { application, score: bestScore, value: bestValue };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score); // Sort by score descending

    if (matchingApplications.length > 0) {
        const bestMatch = matchingApplications[0];

        // Only return suggestion for the current field being typed
        if (fieldName === 'organization_name' && bestMatch.application.organization_name) {
            suggestions.organization_name = bestMatch.application.organization_name;
        } else if (fieldName === 'current_members_count' && bestMatch.application.current_members_count) {
            suggestions.current_members_count = bestMatch.application.current_members_count;
        } else if (fieldName === 'president_name' && bestMatch.application.president_name) {
            suggestions.president_name = bestMatch.application.president_name;
        } else if (fieldName === 'president_email' && bestMatch.application.president_email) {
            suggestions.president_email = bestMatch.application.president_email;
        } else if (fieldName === 'president_phone' && bestMatch.application.president_phone) {
            suggestions.president_phone = bestMatch.application.president_phone;
        } else if (fieldName === 'secretary_name' && bestMatch.application.secretary_name) {
            suggestions.secretary_name = bestMatch.application.secretary_name;
        } else if (fieldName === 'secretary_email' && bestMatch.application.secretary_email) {
            suggestions.secretary_email = bestMatch.application.secretary_email;
        } else if (fieldName === 'secretary_phone' && bestMatch.application.secretary_phone) {
            suggestions.secretary_phone = bestMatch.application.secretary_phone;
        } else if (fieldName === 'treasurer_name' && bestMatch.application.treasurer_name) {
            suggestions.treasurer_name = bestMatch.application.treasurer_name;
        } else if (fieldName === 'treasurer_email' && bestMatch.application.treasurer_email) {
            suggestions.treasurer_email = bestMatch.application.treasurer_email;
        } else if (fieldName === 'treasurer_phone' && bestMatch.application.treasurer_phone) {
            suggestions.treasurer_phone = bestMatch.application.treasurer_phone;
        } else if (fieldName === 'street_address_1' && bestMatch.application.street_address_1) {
            suggestions.street_address_1 = bestMatch.application.street_address_1;
        } else if (fieldName === 'street_address_2' && bestMatch.application.street_address_2) {
            suggestions.street_address_2 = bestMatch.application.street_address_2;
        } else if (fieldName === 'city' && bestMatch.application.city) {
            suggestions.city = bestMatch.application.city;
        } else if (fieldName === 'state' && bestMatch.application.state) {
            suggestions.state = bestMatch.application.state;
        } else if (fieldName === 'postcode' && bestMatch.application.postcode) {
            suggestions.postcode = bestMatch.application.postcode;
        } else if (fieldName === 'screening_venue' && bestMatch.application.screening_venue) {
            suggestions.screening_venue = bestMatch.application.screening_venue;
        }
    }

    return suggestions;
};

/**
 * Get member-specific auto-fill suggestions
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing members
 * @returns {Object} Auto-fill suggestions
 */
const getMemberAutoFill = (fieldName, fieldValue, existingData) => {
    const suggestions = {};
    const fieldMappings = {
        organization_name: ['organization_name', 'company_name', 'name'],
        member_id: ['member_id', 'id', 'member_number'],
        excel_id: ['excel_id', 'excel_number', 'spreadsheet_id'],
        member_count: ['member_count', 'members_count', 'current_members_count'],
        contact_person: ['contact_person', 'president_name', 'representative'],
        president_name: ['president_name', 'contact_person', 'representative'],
        president_email: ['president_email', 'contact_email', 'email'],
        president_phone: ['president_phone', 'contact_phone', 'phone'],
        secretary_name: ['secretary_name', 'contact_person', 'representative'],
        secretary_email: ['secretary_email', 'contact_email', 'email'],
        secretary_phone: ['secretary_phone', 'contact_phone', 'phone'],
        treasurer_name: ['treasurer_name', 'contact_person', 'representative'],
        treasurer_email: ['treasurer_email', 'contact_email', 'email'],
        treasurer_phone: ['treasurer_phone', 'contact_phone', 'phone'],
        street_address_1: ['street_address_1', 'address', 'business_address'],
        street_address_2: ['street_address_2', 'address_2'],
        city: ['city', 'town', 'municipality'],
        state: ['state', 'province', 'region'],
        postcode: ['postcode', 'postal_code', 'zip_code'],
        country: ['country', 'nation'],
        screening_frequency: ['screening_frequency', 'frequency'],
        screening_venue: ['screening_venue', 'venue', 'location'],
        website_url: ['website_url', 'website', 'url'],
        facebook_url: ['facebook_url', 'facebook'],
        instagram_url: ['instagram_url', 'instagram'],
        twitter_url: ['twitter_url', 'twitter'],
        industry: ['industry', 'sector', 'business_type']
    };

    // Find matching members with scoring
    const matchingMembers = existingData
        .map(member => {
            const searchFields = fieldMappings[fieldName] || [fieldName];
            let bestScore = 0;
            let bestValue = null;

            searchFields.forEach(field => {
                if (member[field]) {
                    const score = calculateMatchScore(fieldValue, member[field].toString(), fieldName);
                    if (score > bestScore) {
                        bestScore = score;
                        bestValue = member[field];
                    }
                }
            });

            return { member, score: bestScore, value: bestValue };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score); // Sort by score descending

    if (matchingMembers.length > 0) {
        const bestMatch = matchingMembers[0];

        // Only return suggestion for the current field being typed
        if (fieldName === 'organization_name' && bestMatch.member.organization_name) {
            suggestions.organization_name = bestMatch.member.organization_name;
        } else if (fieldName === 'member_id' && bestMatch.member.member_id) {
            suggestions.member_id = bestMatch.member.member_id;
        } else if (fieldName === 'excel_id' && bestMatch.member.excel_id) {
            suggestions.excel_id = bestMatch.member.excel_id;
        } else if (fieldName === 'member_count' && bestMatch.member.member_count) {
            suggestions.member_count = bestMatch.member.member_count;
        } else if (fieldName === 'contact_person' && bestMatch.member.contact_person) {
            suggestions.contact_person = bestMatch.member.contact_person;
        } else if (fieldName === 'president_name' && bestMatch.member.president_name) {
            suggestions.president_name = bestMatch.member.president_name;
        } else if (fieldName === 'president_email' && bestMatch.member.president_email) {
            suggestions.president_email = bestMatch.member.president_email;
        } else if (fieldName === 'president_phone' && bestMatch.member.president_phone) {
            suggestions.president_phone = bestMatch.member.president_phone;
        } else if (fieldName === 'secretary_name' && bestMatch.member.secretary_name) {
            suggestions.secretary_name = bestMatch.member.secretary_name;
        } else if (fieldName === 'secretary_email' && bestMatch.member.secretary_email) {
            suggestions.secretary_email = bestMatch.member.secretary_email;
        } else if (fieldName === 'secretary_phone' && bestMatch.member.secretary_phone) {
            suggestions.secretary_phone = bestMatch.member.secretary_phone;
        } else if (fieldName === 'treasurer_name' && bestMatch.member.treasurer_name) {
            suggestions.treasurer_name = bestMatch.member.treasurer_name;
        } else if (fieldName === 'treasurer_email' && bestMatch.member.treasurer_email) {
            suggestions.treasurer_email = bestMatch.member.treasurer_email;
        } else if (fieldName === 'treasurer_phone' && bestMatch.member.treasurer_phone) {
            suggestions.treasurer_phone = bestMatch.member.treasurer_phone;
        } else if (fieldName === 'street_address_1' && bestMatch.member.street_address_1) {
            suggestions.street_address_1 = bestMatch.member.street_address_1;
        } else if (fieldName === 'street_address_2' && bestMatch.member.street_address_2) {
            suggestions.street_address_2 = bestMatch.member.street_address_2;
        } else if (fieldName === 'city' && bestMatch.member.city) {
            suggestions.city = bestMatch.member.city;
        } else if (fieldName === 'state' && bestMatch.member.state) {
            suggestions.state = bestMatch.member.state;
        } else if (fieldName === 'postcode' && bestMatch.member.postcode) {
            suggestions.postcode = bestMatch.member.postcode;
        } else if (fieldName === 'country' && bestMatch.member.country) {
            suggestions.country = bestMatch.member.country;
        } else if (fieldName === 'screening_frequency' && bestMatch.member.screening_frequency) {
            suggestions.screening_frequency = bestMatch.member.screening_frequency;
        } else if (fieldName === 'screening_venue' && bestMatch.member.screening_venue) {
            suggestions.screening_venue = bestMatch.member.screening_venue;
        } else if (fieldName === 'website_url' && bestMatch.member.website_url) {
            suggestions.website_url = bestMatch.member.website_url;
        } else if (fieldName === 'facebook_url' && bestMatch.member.facebook_url) {
            suggestions.facebook_url = bestMatch.member.facebook_url;
        } else if (fieldName === 'instagram_url' && bestMatch.member.instagram_url) {
            suggestions.instagram_url = bestMatch.member.instagram_url;
        } else if (fieldName === 'twitter_url' && bestMatch.member.twitter_url) {
            suggestions.twitter_url = bestMatch.member.twitter_url;
        } else if (fieldName === 'industry' && bestMatch.member.industry) {
            suggestions.industry = bestMatch.member.industry;
        }
    }

    return suggestions;
};

/**
 * Get user-specific auto-fill suggestions
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing users
 * @returns {Object} Auto-fill suggestions
 */
const getUserAutoFill = (fieldName, fieldValue, existingData) => {
    const suggestions = {};
    const fieldMappings = {
        username: ['username', 'user_name', 'login'],
        email: ['email', 'email_address', 'contact_email'],
        first_name: ['first_name', 'fname', 'given_name'],
        last_name: ['last_name', 'lname', 'surname'],
        phone: ['phone', 'phone_number', 'contact_phone'],
        address: ['address', 'home_address', 'location']
    };

    // Find matching users with scoring
    const matchingUsers = existingData
        .map(user => {
            const searchFields = fieldMappings[fieldName] || [fieldName];
            let bestScore = 0;
            let bestValue = null;

            searchFields.forEach(field => {
                if (user[field]) {
                    const score = calculateMatchScore(fieldValue, user[field].toString(), fieldName);
                    if (score > bestScore) {
                        bestScore = score;
                        bestValue = user[field];
                    }
                }
            });

            return { user, score: bestScore, value: bestValue };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score); // Sort by score descending

    if (matchingUsers.length > 0) {
        const bestMatch = matchingUsers[0];

        // Only return suggestion for the current field being typed
        if (fieldName === 'username' && bestMatch.user.username) {
            suggestions.username = bestMatch.user.username;
        } else if (fieldName === 'email' && bestMatch.user.email) {
            suggestions.email = bestMatch.user.email;
        } else if (fieldName === 'first_name' && bestMatch.user.first_name) {
            suggestions.first_name = bestMatch.user.first_name;
        } else if (fieldName === 'last_name' && bestMatch.user.last_name) {
            suggestions.last_name = bestMatch.user.last_name;
        } else if (fieldName === 'phone' && bestMatch.user.phone) {
            suggestions.phone = bestMatch.user.phone;
        } else if (fieldName === 'address' && bestMatch.user.address) {
            suggestions.address = bestMatch.user.address;
        }
    }

    return suggestions;
};

/**
 * Calculate exact start match score
 * @param {string} input - The input value
 * @param {string} suggestion - The suggestion value
 * @param {number} inputLength - Length of input
 * @returns {number} Score or 0 if no match
 */
const calculateExactStartScore = (input, suggestion, inputLength) => {
    if (suggestion.startsWith(input)) {
        const baseScore = 85;
        const lengthBonus = Math.min(inputLength * 2, 15); // Up to 15 bonus points
        return Math.min(baseScore + lengthBonus, 99);
    }
    return 0;
};

/**
 * Calculate contains match score
 * @param {string} input - The input value
 * @param {string} suggestion - The suggestion value
 * @param {number} inputLength - Length of input
 * @returns {number} Score or 0 if no match
 */
const calculateContainsScore = (input, suggestion, inputLength) => {
    if (suggestion.includes(input)) {
        const position = suggestion.indexOf(input);
        const positionBonus = position === 0 ? 20 : Math.max(0, 20 - position);
        const lengthBonus = Math.min(inputLength * 1.5, 10); // Up to 10 bonus points
        const baseScore = 60;
        return Math.min(baseScore + positionBonus + lengthBonus, 95);
    }
    return 0;
};

/**
 * Calculate word boundary match score
 * @param {string} input - The input value
 * @param {string} suggestion - The suggestion value
 * @param {number} inputLength - Length of input
 * @returns {number} Score or 0 if no match
 */
const calculateWordBoundaryScore = (input, suggestion, inputLength) => {
    const words = suggestion.split(/\s+/);
    const hasWordMatch = words.some(word => word.startsWith(input));
    if (hasWordMatch) {
        const baseScore = 50;
        const lengthBonus = Math.min(inputLength * 1.2, 8); // Up to 8 bonus points
        return Math.min(baseScore + lengthBonus, 85);
    }
    return 0;
};

/**
 * Calculate fuzzy match score
 * @param {string} input - The input value
 * @param {string} suggestion - The suggestion value
 * @param {number} inputLength - Length of input
 * @returns {number} Score or 0 if no match
 */
const calculateFuzzyScore = (input, suggestion, inputLength) => {
    const similarity = calculateSimilarity(input, suggestion);
    if (similarity > 0.7) { // Higher threshold for fuzzy matching
        const baseScore = 30;
        const lengthBonus = Math.min(inputLength * 0.8, 5); // Up to 5 bonus points
        return Math.min(baseScore + Math.round(similarity * 20) + lengthBonus, 75);
    }
    return 0;
};

/**
 * Calculate partial word match score
 * @param {string} input - The input value
 * @param {string} suggestion - The suggestion value
 * @param {number} inputLength - Length of input
 * @returns {number} Score or 0 if no match
 */
const calculatePartialWordScore = (input, suggestion, inputLength) => {
    if (inputLength >= 4) {
        const words = suggestion.split(/\s+/);
        const hasPartialMatch = words.some(word =>
            word.length > input.length && word.includes(input)
        );
        if (hasPartialMatch) {
            const baseScore = 25;
            const lengthBonus = Math.min(inputLength * 0.5, 3); // Up to 3 bonus points
            return Math.min(baseScore + lengthBonus, 50);
        }
    }
    return 0;
};

/**
 * Calculate match score for ranking suggestions with progressive accuracy
 * @param {string} inputValue - The value being typed
 * @param {string} suggestionValue - The suggested value
 * @param {string} fieldName - The field name for context
 * @returns {number} Match score (0-100)
 */
const calculateMatchScore = (inputValue, suggestionValue, fieldName) => {
    if (!inputValue || !suggestionValue) return 0;

    const input = inputValue.toLowerCase().trim();
    const suggestion = suggestionValue.toLowerCase().trim();
    const inputLength = input.length;

    // Exact match gets highest score
    if (input === suggestion) return 100;

    // Check different match types in order of preference
    const exactStartScore = calculateExactStartScore(input, suggestion, inputLength);
    if (exactStartScore > 0) return exactStartScore;

    const containsScore = calculateContainsScore(input, suggestion, inputLength);
    if (containsScore > 0) return containsScore;

    const wordBoundaryScore = calculateWordBoundaryScore(input, suggestion, inputLength);
    if (wordBoundaryScore > 0) return wordBoundaryScore;

    // Only use fuzzy matching for longer inputs
    if (inputLength >= 3) {
        const fuzzyScore = calculateFuzzyScore(input, suggestion, inputLength);
        if (fuzzyScore > 0) return fuzzyScore;
    }

    // Partial word match - only for longer inputs
    const partialWordScore = calculatePartialWordScore(input, suggestion, inputLength);
    if (partialWordScore > 0) return partialWordScore;

    return 0;
};

/**
 * Calculate string similarity using Levenshtein distance
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score (0-1)
 */
const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
};

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance
 */
const levenshteinDistance = (str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1
                );
            }
        }
    }

    return matrix[str2.length][str1.length];
};

/**
 * Get minimum character threshold (always 2 for progressive accuracy)
 * @param {string} fieldName - The field name
 * @param {string} dataType - The data type context
 * @returns {number} Minimum character threshold
 */
const getMinimumThreshold = (fieldName, dataType) => {
    return 2; // Always use 2 characters as minimum threshold for progressive accuracy
};

/**
 * Get auto-fill suggestions with debouncing
 * @param {string} fieldName - The field being filled
 * @param {string} fieldValue - The value being entered
 * @param {Array} existingData - Array of existing records
 * @param {Object} currentFormData - Current form data
 * @param {Object} validationRules - Validation rules
 * @param {string} dataType - Type of data (distributor, application, member, user)
 * @returns {Object} Auto-fill suggestions
 */
export const getAutoFillSuggestions = (fieldName, fieldValue, existingData = [], currentFormData = {}, validationRules = {}, dataType = 'generic') => {
    // Get minimum threshold (always 2 for progressive accuracy)
    const minThreshold = getMinimumThreshold(fieldName, dataType);

    // Use 2-character threshold for progressive accuracy
    if (!fieldValue || fieldValue.trim().length < minThreshold) {
        return {};
    }

    let suggestions = {};

    // Use data type context instead of field name patterns
    switch (dataType) {
        case 'distributor':
            suggestions = getDistributorAutoFill(fieldName, fieldValue, existingData);
            break;
        case 'application':
            suggestions = getApplicationAutoFill(fieldName, fieldValue, existingData);
            break;
        case 'member':
            suggestions = getMemberAutoFill(fieldName, fieldValue, existingData);
            break;
        case 'user':
            suggestions = getUserAutoFill(fieldName, fieldValue, existingData);
            break;
        default:
            // Fallback to generic field-based matching
            suggestions = findAutoFillSuggestions(fieldName, fieldValue, existingData, currentFormData);
    }

    // Validate suggestions
    return validateAutoFillSuggestions(suggestions, validationRules);
};
